import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { VALUES } from "@/data/content";
import { ShieldCheck, Award, BookOpen, Users, Flame, MapPin } from "lucide-react";

const VALUE_ICONS = [ShieldCheck, Award, BookOpen, Users, Flame];

export default function About() {
  const { site } = useSite();
  const c = site.content;

  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="The Program"
        title="About Bears Baseball"
        subtitle={c.subtitle_about}
        testid="about-header"
      />

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">Our Story</span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mt-3 leading-none">A Norwalk Tradition</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base">{c.about_story_1}</p>
            <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base">{c.about_story_2}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-lg overflow-hidden shadow-xl border border-slate-200">
              <img src="/assets/team-field.jpeg" alt="Norwalk Bears team" className="w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mb-2">Program Values</h2>
            <p className="text-slate-600 mb-10 text-sm sm:text-base">Five standards every Bear lives by.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <Reveal key={v.title} delay={i * 0.07}>
                  <div className="card-lift bg-white rounded-lg border border-slate-200 p-6 h-full hover:border-[#0F4C2A]/40" data-testid={`value-card-${i}`}>
                    <Icon size={26} className="text-[#D97706] mb-4" />
                    <h3 className="font-condensed text-xl font-bold uppercase text-slate-900">{v.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white" data-testid="community-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img src="/assets/community-certs.webp" alt="Bears players recognized for Character Partnership at Tracey Magnet School" className="rounded-lg object-cover h-64 w-full shadow-md" />
                <img src="/assets/community-reading.webp" alt="Bears players reading to elementary students" className="rounded-lg object-cover h-64 w-full shadow-md mt-8" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">Community First</span>
              <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mt-3 leading-none">{c.community_title}</h2>
              <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base">{c.community_text}</p>
              <div className="mt-6 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-5">
                <MapPin size={20} className="text-[#0F4C2A] shrink-0 mt-0.5" />
                <div>
                  <p className="font-condensed text-lg font-bold uppercase text-slate-900">{c.field_name}</p>
                  <p className="text-sm text-slate-600">Home of Bears Baseball — {c.contact_school}, {c.contact_address}.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
